import { types, flow, getEnv, SnapshotOut } from 'mobx-state-tree';

import {
    translation,
    //numberFormatter
} from 'easit--localization';
import { ProgressStateModel, ICommonAppEnvWithSBBaseApi, ProgressStateModelForStore, ICommonAppEnvWithVirtualGames } from '../index';
import { errorIsCancel, TicketListTimeFilterRequest, IApiDate2StringConverter } from '../../api/apiBase';
import { IGroupedApiCtx } from '../../api/apiCommon';
import { ListPagerModel } from '../listPager';
import { translationForMts } from '../../../../localization/localization';
import { TicketListItem, TicketListTimeFilterModelForStore } from '../ticketLists/baseTicketListStore';
import { TicketListTimeFilterModel } from '../ticketLists/ticketListStore';
import { ITicketListForVGResponse, ITicketListForVGResponseItem } from '../../dtos/ticketForVirtualGamesDtos';
import { observable } from 'mobx';
import { virtualGamesLoadHelper } from './ticketDetailForVG';
import { OfferType } from 'eTip--common';
import { IVirtualGamesApi } from '../../apiVirtualgames/apiVirtualGames';

export const TicketListForVGStore =
    types.model({
        tickets: types.optional(types.array(TicketListItem), []), //for compatibility with view
        totalTicketsCount: types.optional(types.number, 0),
        error: types.maybeNull(types.string),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        isLastActionSetFilter: types.optional(types.boolean, false),
        timeFilter: types.optional(TicketListTimeFilterModelForStore, TicketListTimeFilterModel.Month),
        pager: types.maybeNull(ListPagerModel)
    }).views(self => {

        const t = translationForMts();
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const timeFilterOptionsPrepared = [
            { key: TicketListTimeFilterModel.None, title: t.ticketList_TimeFilter_None },
            { key: TicketListTimeFilterModel.Day, title: t.ticketList_TimeFilter_Day },
            { key: TicketListTimeFilterModel.ThreeDays, title: t.ticketList_TimeFilter_ThreeDays },
            { key: TicketListTimeFilterModel.Week, title: t.ticketList_TimeFilter_Week },
            { key: TicketListTimeFilterModel.TwoWeeks, title: t.ticketList_TimeFilter_TwoWeeks },
            { key: TicketListTimeFilterModel.Month, title: t.ticketList_TimeFilter_Month }
        ];

        return {
            get currency() {
                return getEnv<ICommonAppEnvWithSBBaseApi>(self).settings.currency;
            },

            get isUserLogged() {
                return env.loggedUserId > 0;
            },
            get timeFilterOptions(): { key: TicketListTimeFilterModel, title: string }[] {
                return timeFilterOptionsPrepared;
            },
        }
    }).actions(self => {

        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        const envVG = getEnv<ICommonAppEnvWithVirtualGames>(self);

        let synchroFullCtx: IGroupedApiCtx<IVirtualGamesApi> | null = null;
        let synchroNextCtx: IGroupedApiCtx<IVirtualGamesApi> | null = null;
        const ticketsPerPage = 12;

        const loadDataAsync: (isSilent: boolean) => Promise<void> = flow(function* (isSilent: boolean) {

            if (env.loggedUserId <= 0) {
                self.state = ProgressStateModel.Failed;
                self.error = translationForMts().myTickets_Login;
                return;
            }

            let ctx: IGroupedApiCtx<IVirtualGamesApi> | null = null;
            try {

                if (synchroFullCtx && synchroFullCtx.isOpen()) {
                    synchroFullCtx.cancel();
                }
                if (synchroNextCtx && synchroNextCtx.isOpen()) {
                    synchroNextCtx.cancel();
                    synchroNextCtx = null;
                }

                ctx = envVG.virtualGamesApi.open();
                synchroFullCtx = ctx;

                if (!isSilent) {
                    self.state = ProgressStateModel.Loading;
                }

                self.error = null;


                let filterForApi = prepareFilterForApi();
                filterForApi.rowsOfPage = ticketsPerPage;
                filterForApi.indexOfPage = self.pager.currentPage;

                const response: ITicketListForVGResponse = yield ctx.api.getTicketListForVGAsync(filterForApi);

                self.totalTicketsCount = response.count;
                setTickets(response.tickets, synchroFullCtx.api.toDate);
                preparePager();

                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                self.state = ProgressStateModel.Failed;
                self.error = translation().bet_ErrorInTicketListLoad;
                throw error;
            } finally {
                if (synchroFullCtx === ctx) {
                    synchroFullCtx = null;
                }
            }
        });

        const setTickets = (tickets: ITicketListForVGResponseItem[], string2DateConverter: IApiDate2StringConverter): void => {
            self.tickets = observable([]);
            if (tickets && tickets.length) {
                tickets.forEach(ticket => {
                    self.tickets.push(
                        TicketListItem.create(<SnapshotOut<typeof TicketListItem>>{
                            id: ticket.barcode, //long
                            secureId: ticket.barcode.toString(),
                            offerType: OfferType.Prematch,
                            evalState: virtualGamesLoadHelper.prepareSelectionEvalState(ticket.status),
                            ticketState: virtualGamesLoadHelper.prepareTicketState(ticket.status),
                            stake: ticket.stake,
                            stakePoints: null,
                            potentialWin: ticket.win ? ticket.win : -1,
                            prizeEval: null,
                            placementDate: string2DateConverter(ticket.createdDate).getTime(),
                            number: ticket.barcode.toString(),
                            cashout: null
                        })
                    );
                });
            }
        }

        const preparePager = (): void => {
            const currentPage = self.pager.currentPage;
            const totalPages = Math.ceil(self.totalTicketsCount / ticketsPerPage);
            const pageStart = Math.max(currentPage - 3, 1);
            const pagesEnd = Math.min(currentPage + 3, totalPages);
            self.pager = ListPagerModel.create(<SnapshotOut<typeof ListPagerModel>>{
                currentPage: currentPage,
                pagesStart: pageStart,
                pagesEnd: pagesEnd,
                totalPages: totalPages
            });
        }

        const setFilterAsync = (timeFilter: TicketListTimeFilterModel): Promise<void> => {
            self.timeFilter = timeFilter;
            self.pager = ListPagerModel.create(<SnapshotOut<typeof ListPagerModel>>{});
            self.isLastActionSetFilter = true;
            return loadDataAsync(false);
        }

        const prepareFilterForApi = (): any => {
            let timeFilter: TicketListTimeFilterRequest | undefined;

            if (self.timeFilter) {
                switch (self.timeFilter) {
                    case TicketListTimeFilterModel.None:
                    case TicketListTimeFilterModel.All: timeFilter = undefined; break; //ignore
                    case TicketListTimeFilterModel.Day: timeFilter = TicketListTimeFilterRequest.Day; break;
                    case TicketListTimeFilterModel.ThreeDays: timeFilter = TicketListTimeFilterRequest.ThreeDays; break;
                    case TicketListTimeFilterModel.Week: timeFilter = TicketListTimeFilterRequest.Week; break;
                    case TicketListTimeFilterModel.TwoWeeks: timeFilter = TicketListTimeFilterRequest.TwoWeeks; break;
                    case TicketListTimeFilterModel.Month: timeFilter = TicketListTimeFilterRequest.Month; break;
                    default: throw new Error("Unexpected time filter " + self.timeFilter);
                }
            } else {
                timeFilter = undefined;
            }

            return {
                timeFilter: timeFilter,
            };
        }

        const setPagerPageAsync = (pageIndex: number): Promise<void> => {
            self.pager.setCurrentPage(pageIndex);
            self.isLastActionSetFilter = false;
            return loadDataAsync(false);
        }

        return {
            loadDataAsync,
            setPagerPageAsync,
            setFilterAsync
        }
    });


